/*
 * des-vat-toggle.js — De Egaline Schuur VAT (BTW) display toggle.
 * -----------------------------------------------------------------------------
 * Display-only incl./excl. BTW switch. It:
 *   - resolves the mode from localStorage['des_vat'], else the theme-setting
 *     default seeded on the control via [data-des-vat-default],
 *   - reflects it on <html data-des-vat="incl|excl"> and on the toggle buttons,
 *   - swaps every price node's text between its data-price-incl / data-price-excl
 *     values, and every [data-des-vat-label] between data-label-incl/excl,
 *   - persists the user's choice, and dispatches a `des:vat-change` event.
 *
 * PRESENTATIONAL ONLY. The excl. figures are gross-derived values emitted by the
 * price snippets (snippets/price.liquid; later cart-products/cart-summary). This
 * script never changes a variant price, the cart, or Shopify checkout tax — it
 * only swaps already-rendered display strings (ADR 0001 §4/§5).
 *
 * A MutationObserver re-applies the current mode when Horizon re-renders price
 * nodes — including IN-PLACE morphs (product-card swatch updates and cart SRA
 * morphs that rewrite text or the data-price-* attributes without adding/removing
 * children). It therefore watches childList + characterData + the price
 * attributes, and also re-applies on shopify:cart:lines-update as a backup. Writes
 * are guarded (only when the text actually differs) so the observer converges
 * without looping (we never write the data-price-* attributes ourselves).
 *
 * Namespaced, no globals leaked. Safe to run more than once.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "des_vat";
  var ATTR = "data-des-vat";
  var VALID = { incl: true, excl: true };

  var currentMode = "incl";
  var rafId = null;

  function readStored() {
    try {
      var v = window.localStorage.getItem(STORAGE_KEY);
      return VALID[v] ? v : null;
    } catch (e) {
      return null; // private mode / storage disabled — fall back to default
    }
  }

  function store(mode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* non-fatal */
    }
  }

  function resolveDefault() {
    var el = document.querySelector("[data-des-vat-default]");
    var d = el && el.getAttribute("data-des-vat-default");
    return VALID[d] ? d : "incl";
  }

  // Guarded write: only touch the DOM when the value actually changes, so the
  // MutationObserver below cannot enter a write→observe→write loop.
  function setText(el, value) {
    if (value != null && el.textContent !== value) {
      el.textContent = value;
    }
  }

  function applyControls(mode) {
    document.documentElement.setAttribute(ATTR, mode);
    var btns = document.querySelectorAll("[data-des-vat-mode]");
    for (var i = 0; i < btns.length; i++) {
      var isActive = btns[i].getAttribute("data-des-vat-mode") === mode;
      btns[i].classList.toggle("is-active", isActive);
      btns[i].setAttribute("aria-pressed", isActive ? "true" : "false");
    }
  }

  function applyPrices(mode) {
    var priceAttr = mode === "excl" ? "data-price-excl" : "data-price-incl";
    var prices = document.querySelectorAll("[data-des-price]");
    for (var i = 0; i < prices.length; i++) {
      setText(prices[i], prices[i].getAttribute(priceAttr));
    }
    var labelAttr = mode === "excl" ? "data-label-excl" : "data-label-incl";
    var labels = document.querySelectorAll("[data-des-vat-label]");
    for (var j = 0; j < labels.length; j++) {
      setText(labels[j], labels[j].getAttribute(labelAttr));
    }
  }

  function apply(mode) {
    currentMode = mode;
    applyControls(mode);
    applyPrices(mode);
    // Notify any other display islands. This module changes no real prices.
    document.dispatchEvent(new CustomEvent("des:vat-change", { detail: { mode: mode } }));
  }

  function setMode(mode) {
    if (!VALID[mode]) return;
    store(mode);
    apply(mode);
  }

  function bind() {
    var btns = document.querySelectorAll("[data-des-vat-mode]");
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].dataset.desVatBound === "true") continue; // idempotent
      btns[i].dataset.desVatBound = "true";
      btns[i].addEventListener("click", function (e) {
        setMode(e.currentTarget.getAttribute("data-des-vat-mode"));
      });
    }
  }

  // Re-apply prices/labels to nodes Horizon re-renders. Product-card swatch updates
  // and cart morphs can rewrite an existing price node IN PLACE (text/attribute
  // changes, no child add/remove), so we watch childList AND characterData AND the
  // price data-attributes — otherwise a variant switch in excl. mode would revert to
  // the server-rendered incl. value. Guarded writes (and never writing data-price-*)
  // keep this from looping; bursts coalesce into one pass per frame.
  function observe() {
    if (!("MutationObserver" in window) || !document.body) return;
    var mo = new MutationObserver(function () {
      if (rafId) return;
      rafId = window.requestAnimationFrame(function () {
        rafId = null;
        applyPrices(currentMode);
      });
    });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["data-price-incl", "data-price-excl"],
    });
  }

  function init() {
    currentMode = readStored() || resolveDefault();
    apply(currentMode);
    bind();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
      observe();
    });
  } else {
    init();
    observe();
  }

  // Re-apply after the theme editor re-renders the section.
  document.addEventListener("shopify:section:load", init);

  // Belt-and-suspenders: re-apply after Horizon dispatches a cart update
  // (documented in docs/spike/horizon-vat-cart-micro-spike.md). Harmless if the
  // event never fires; the MutationObserver already covers most re-renders.
  document.addEventListener("shopify:cart:lines-update", function () {
    applyPrices(currentMode);
  });
})();
